import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { findValue, FormikSubFieldProps, useFormikFieldStyles } from './FormikField'
import { Field, Input } from '@fluentui/react-components'
import { InputProps } from '@fluentui/react-input'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { TimerRegular } from '@fluentui/react-icons'
import { RefAttributes } from '@fluentui/react-utilities'

const format = (value: string = '', required?: boolean): string => {
    let separatorPosition = value.indexOf(':')
    if ((!required && !value) || value === '') {
        return ''
    }

    // No if-else statement on purpose, because both operations might be triggered.
    // Make sure a colon exists in the entity.
    if (separatorPosition === -1) {
        const hours = value.substring(0, value.length - 2)
        const minutes = (value.length < 2 ? new Array(2 - value.length).fill('0').join('') : '') + value.substring(value.length - 2)

        value = hours + ':' + minutes
        separatorPosition = value.indexOf(':')
    }
    // Ensure that the minutes has 2 digits at least.
    if (value.length - (separatorPosition + 1) < 2) {
        value = value + new Array(2 - (value.length - (separatorPosition + 1))).fill('0').join('')
    }

    return value
        .replace(/[.,]/, ':') // Allow dots and commas as valid separators.
        .replace(/[^\d:]/, '') // Only accept numbers and colons.
        .replace(/.*?([\d]{0,2}[:][\d]{0,2}).*/, '$1') // Limit the format to HH:MM.
        .replace(/[0]+([\d]+[:])/, '$1') // Remove excess preceding 0.
        .replace(/^[:]/, '0:') // Prepend a 0, if there is only a colon.
}

export default function FormikDurationField<Values>(props: FormikSubFieldProps<InputProps & RefAttributes<InputProps>, Values>): ReactElement {
    const styles = useFormikFieldStyles()
    const { t: translate } = useTranslation()

    const { values, errors, setFieldValue } = useFormikContext<Values>()

    const validationMessage = useMemo(() => findValue(errors, props.fieldName as string), [values, errors])
    const validationState = useMemo(() => (findValue(errors, props.fieldName as string) ? 'error' : 'none'), [values, errors])
    const currentValue = useMemo(() => findValue(values, props.fieldName as string) || '', [values])

    return (
        <Field
            label={props.labelKey ? translate(props.labelKey) : undefined}
            hint={props.hint ?? (props.hintKey ? translate(props.hintKey) : undefined)}
            {...props}
            validationMessage={validationMessage}
            validationState={validationState}
            className={props.className ?? (!props.renderLabel && !props.labelKey && styles.noLabel)}
        >
            <Input
                required={props.required}
                onChange={(e, data) => {
                    setFieldValue(props.fieldName as string, data.value ?? '')
                }}
                onBlur={e => {
                    const value = format(e.currentTarget.value, props.required)
                    setFieldValue(props.fieldName as string, value)
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        const value = format(e.currentTarget.value, props.required)
                        setFieldValue(props.fieldName as string, value)
                    }
                }}
                value={currentValue}
                autoComplete="off"
                data-lpignore={'true'}
                contentAfter={<TimerRegular />}
                {...(props.componentProps as any)}
            />
        </Field>
    )
}
